/**
 * Staff detail screen (DNKADM-0053 U2) — one `v2_staff` member with the
 * **location-tabbed weekly schedule** editor, the **date-exceptions** panel, and the
 * **appointment-caps** panel (handoff §5.6). Routed at `/staff/:staffId` under
 * `RequireAdmin`.
 *
 * Loads the staff member (`getStaffMember`), their assigned locations
 * (`getStaffLocations` → resolved to the active owned `listLocations`), their date
 * exceptions, and their caps. Schedules are authored **per (staff, location)**, so a
 * **tab per assigned location** hosts that location's weekly grid (Sun→Sat) — the
 * schedule resource refetches when the active tab (location) changes. A staffer with
 * no assigned locations shows a clear note (assign a location via Edit details
 * first). A load failure shows error + Retry; a missing staffer (`404`) shows a
 * not-found note with a link back to the list.
 *
 * **Base-field editing (DNKADM-0286).** The header hosts an "Edit details" button that
 * opens the existing {@link StaffDialog} (name/email/phone/assigned locations) against
 * the loaded record — the detail page is the single place to view AND edit; the list's
 * quick-edit trigger is retired. A save refetches the record and the assigned
 * locations (the schedule tabs follow).
 *
 * **Linked login (DNKADM-0362).** The header details show which staff login this coach
 * record belongs to — `authUserId` resolved against the Team & access roster to
 * `name (email)`, or "Not linked". The raw id is never rendered. The roster load lives
 * here, and the SAME resolved options are handed to {@link StaffDialog} so opening
 * "Edit details" does not fetch the roster a second time.
 *
 * **Calendar sync (DNKADM-0384 U2/U3).** The last section hosts
 * {@link StaffCalendarSyncPanel}: the per-coach sync switch + the calendar picked by
 * NAME from what the tenant's provider offers, and the calendar test. It is keyed on the
 * record's calendar fields so a save (refetch) reseeds it; the test's pending removal
 * survives that remount in its own in-memory store.
 */
import { useCallback, useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Link as MuiLink,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import {
  getStaffMember,
  getStaffSchedule,
  getStaffLocations,
  listStaffDateExceptions,
  listStaffCaps,
  staffDisplayName,
} from '@/api/staff';
import { listLocations, type V2Location } from '@/api/locations';
import { getPlatformMembers } from '@/api/platformMembers';
import { StaffScheduleGrid } from './StaffScheduleGrid';
import { StaffDateExceptionsPanel } from './StaffDateExceptionsPanel';
import { StaffCapsPanel } from './StaffCapsPanel';
import { StaffCalendarSyncPanel } from './StaffCalendarSyncPanel';
import { StaffDialog } from './StaffDialog';
import {
  resolveStaffLoginLabel,
  toStaffLoginOptions,
  type StaffLoginOption,
} from './staffLogins';
import { ScreenError } from '@/components/ScreenError';

/**
 * The header's linked-login copy: "Not linked" when the coach has no link, the roster
 * label (`name (email)`) when it resolves, and `null` — render nothing — while the
 * roster is still loading, when it failed, or when the linked login has left the
 * roster. The `authUserId` itself is NEVER shown; a bare platform id tells an operator
 * nothing and is not what this line is for.
 */
function staffLoginLine(
  authUserId: number | null,
  options: StaffLoginOption[] | null,
): string | null {
  if (authUserId === null) return 'Not linked';
  if (options === null) return null;
  return resolveStaffLoginLabel(options, authUserId);
}

export function StaffDetail() {
  const { staffId = '' } = useParams<{ staffId: string }>();
  const showToast = useToast();

  const staff = useResource(
    useCallback(() => getStaffMember(staffId), [staffId]),
    [staffId],
  );

  // Resolve the staffer's assigned locations to full V2Location rows (for tab labels
  // and the exception location options). A failure degrades to no locations.
  const assigned = useResource(
    useCallback(async (): Promise<V2Location[]> => {
      const [ids, all] = await Promise.all([
        getStaffLocations(staffId),
        listLocations({ status: 'active' }),
      ]);
      const order = new Map(all.map((l, i) => [l.id, i] as const));
      return all.filter((l) => ids.includes(l.id)).sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }, [staffId]),
    [staffId],
  );

  // The staff logins this coach record could belong to (DNKADM-0362) — loaded ONCE
  // here and shared with the edit dialog below. A failure degrades to "no options":
  // the header shows no login line rather than guessing, and the picker offers none.
  const logins = useResource(
    useCallback(async (): Promise<StaffLoginOption[]> => {
      const roster = await getPlatformMembers();
      return toStaffLoginOptions(roster.members);
    }, []),
    [],
  );
  const loginOptions = logins.data;

  const exceptions = useResource(
    useCallback(() => listStaffDateExceptions(staffId), [staffId]),
    [staffId],
  );
  const caps = useResource(
    useCallback(() => listStaffCaps(staffId), [staffId]),
    [staffId],
  );

  // The active schedule tab = the selected assigned location id.
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const assignedData = assigned.data;
  const assignedLocations = useMemo(() => assignedData ?? [], [assignedData]);
  const effectiveLocationId = useMemo(() => {
    if (activeLocationId !== null && assignedLocations.some((l) => l.id === activeLocationId)) {
      return activeLocationId;
    }
    return assignedLocations[0]?.id ?? null;
  }, [activeLocationId, assignedLocations]);

  // The schedule for the active (staff, location); refetches when the tab changes.
  const schedule = useResource(
    useCallback(
      () =>
        effectiveLocationId === null
          ? Promise.resolve(null)
          : getStaffSchedule(staffId, effectiveLocationId),
      [staffId, effectiveLocationId],
    ),
    [staffId, effectiveLocationId],
  );

  // Base-field editing in-context (DNKADM-0286): the same dialog the list's Add uses,
  // seeded with the loaded record; a save refetches the record + assigned locations.
  const [editOpen, setEditOpen] = useState(false);

  const loginLine =
    staff.data === null ? null : staffLoginLine(staff.data.authUserId, loginOptions);

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/staff"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All staff
      </MuiLink>

      {staff.status === 'loading' && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading staff member"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {staff.status === 'error' && (
        <ScreenError
          error={staff.error}
          what="the staff member"
          onRetry={staff.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That staff member wasn’t found. They may have been removed.{' '}
              <MuiLink component={RouterLink} to="/staff">
                Back to staff
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {staff.status === 'ready' && staff.data && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h6" component="h1">
              {staffDisplayName(staff.data)}
            </Typography>
            <Chip
              size="small"
              label={staff.data.isActive ? 'Active' : 'Inactive'}
              color={staff.data.isActive ? 'success' : 'default'}
              variant={staff.data.isActive ? 'filled' : 'outlined'}
            />
            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon name="edit" />}
              onClick={() => setEditOpen(true)}
            >
              Edit details
            </Button>
          </Stack>
          <Stack spacing={0.25} sx={{ mb: 3 }}>
            {staff.data.email && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {staff.data.email}
              </Typography>
            )}
            {staff.data.phone && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {staff.data.phone}
              </Typography>
            )}
            {loginLine !== null && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {`Login: ${loginLine}`}
              </Typography>
            )}
          </Stack>

          <Divider sx={{ mb: 3 }} />

          {/* ---- Weekly schedule (location-tabbed) ---- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Weekly schedule
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Set the weekly availability for each location this staff member works at, in that
            location’s local time.
          </Typography>

          {assigned.status === 'loading' && (
            <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3, maxWidth: 640 }} />
          )}

          {assigned.status === 'ready' && assignedLocations.length === 0 && (
            <Alert severity="info" sx={{ maxWidth: 620, mb: 4 }}>
              This staff member isn’t assigned to any active locations yet. Assign them to a
              location (Edit details, above) to set a schedule.
            </Alert>
          )}

          {assignedLocations.length > 0 && effectiveLocationId !== null && (
            <Box sx={{ mb: 4 }}>
              <Tabs
                value={effectiveLocationId}
                onChange={(_e, value: string) => setActiveLocationId(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
                aria-label="Schedule location"
              >
                {assignedLocations.map((loc) => (
                  <Tab key={loc.id} value={loc.id} label={loc.name === '' ? loc.id : loc.name} />
                ))}
              </Tabs>

              {schedule.status === 'loading' && (
                <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3, maxWidth: 640 }} />
              )}

              {schedule.status === 'error' && (
                <ScreenError
                  error={schedule.error}
                  what="the schedule for this location"
                  onRetry={schedule.refetch}
                  sx={{ maxWidth: 620, alignItems: 'center' }}
                />
              )}

              {schedule.status === 'ready' && schedule.data && (
                <StaffScheduleGrid
                  // Remount per location / per (re)load so the rows reseed.
                  key={`sched|${effectiveLocationId}|${schedule.data.days
                    .map(
                      (d) =>
                        `${d.dayOfWeek}:${
                          d.isAvailable
                            ? d.shifts.map((s) => `${s.startTime}-${s.endTime}`).join(',')
                            : 'x'
                        }`,
                    )
                    .join('|')}`}
                  days={schedule.data.days}
                  staffId={staffId}
                  locationId={effectiveLocationId}
                  refetch={schedule.refetch}
                  showToast={showToast}
                />
              )}
            </Box>
          )}

          {/* ---- Date exceptions ---- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Date exceptions
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Mark this staff member off on a specific date, or set special hours — across all
            locations or one.
          </Typography>
          {exceptions.status === 'loading' && (
            <Skeleton variant="rounded" height={140} sx={{ borderRadius: 3, maxWidth: 640, mb: 4 }} />
          )}
          {exceptions.status === 'error' && (
            <ScreenError
              error={exceptions.error}
              what="the date exceptions"
              onRetry={exceptions.refetch}
              sx={{ maxWidth: 620, mb: 4, alignItems: 'center' }}
            />
          )}
          {exceptions.status === 'ready' && exceptions.data && (
            <Box sx={{ mb: 4 }}>
              <StaffDateExceptionsPanel
                exceptions={exceptions.data}
                staffId={staffId}
                locations={assignedLocations}
                refetch={exceptions.refetch}
                showToast={showToast}
              />
            </Box>
          )}

          {/* ---- Appointment caps ---- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
            Appointment caps
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Limit how many appointments this staff member can take per day, week, or month. Leave a
            period blank for no limit.
          </Typography>
          {caps.status === 'loading' && (
            <Skeleton variant="rounded" height={200} sx={{ borderRadius: 3, maxWidth: 640 }} />
          )}
          {caps.status === 'error' && (
            <ScreenError
              error={caps.error}
              what="the appointment caps"
              onRetry={caps.refetch}
              sx={{ maxWidth: 620, alignItems: 'center' }}
            />
          )}
          {caps.status === 'ready' && caps.data && (
            <StaffCapsPanel
              caps={caps.data}
              staffId={staffId}
              refetch={caps.refetch}
              showToast={showToast}
            />
          )}

          {/* ---- Calendar sync (DNKADM-0384) ---- */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 4, mb: 0.5 }}>
            Calendar sync
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            Mirror this staff member’s confirmed bookings onto their own calendar, and send a test
            appointment to prove it lands.
          </Typography>
          <StaffCalendarSyncPanel
            // Remount when the saved calendar fields change so the form reseeds after a save.
            key={`cal|${staff.data.calendarId ?? ''}|${String(staff.data.calendarSyncEnabled)}`}
            staff={staff.data}
            refetch={staff.refetch}
            showToast={showToast}
          />

          <StaffDialog
            open={editOpen}
            staff={staff.data}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              staff.refetch();
              assigned.refetch();
            }}
            showToast={showToast}
            // Reuse the roster this screen already resolved — no second fetch. Still
            // loading (or failed) ⇒ `null` ⇒ the dialog loads its own.
            loginOptions={loginOptions ?? undefined}
          />
        </>
      )}
    </Box>
  );
}
